/**
 * Listing collections — a named, curated group of product listings (e.g.
 * "Best Handmade Bags").
 *
 * The collection endpoint returns the collection *and* its members in one call:
 *   /api/v1/listing-collections/{id}?includeListingDetails=<bool>
 * With `includeListingDetails=false` each member carries only its uid
 * (every other field is null); with `true` they're complete product records.
 */

import { ProductDetailsModal } from "./ProductObject";

export interface ListingCollection {
  /** Collection id (uuid). */
  uid: string;
  creatorId: string;
  /** Curator's display name, when the backend sends one. */
  creatorName?: string | null;
  name: string;
  /** Product uids belonging to this collection. */
  productIds: string[];
  bannerUrl?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** One collection plus its resolved member listings. */
export interface ListingCollectionDetail {
  collection: ListingCollection;
  products: ProductDetailsModal[];
}

// ─── Create / update ─────────────────────────────────────────────────────────

/**
 * Body for POST /api/v1/listing-collections. The admin Create Collection form
 * always sends a banner (upload+crop is mandatory there); "Save collection" on a
 * collection page copies the original's banner, which may be absent. The
 * backend accepts a missing banner.
 */
export interface CreateListingCollectionPayload {
  collectionName: string;
  /** UI-side field name; the wire body sends this as `listingIds` per the API spec. */
  productIds: string[];
  bannerUrl?: string;
}

/** Body for PUT /api/v1/listing-collections/{id}. Full replace — the backend
 *  contract takes the same three fields as create, not a partial patch. */
export interface UpdateListingCollectionPayload
  extends CreateListingCollectionPayload {
  uid: string;
}

// Raw shapes from the backend — converted before touching Redux.

/**
 * A member record. With `includeListingDetails=false` the backend sends every
 * field except the id as null (id-only stub). With `includeListingDetails=true`
 * this is a full product record — same shape that productConvertor expects.
 * Both camelCase and snake_case id fields are tolerated (backend sends both).
 */
export interface RawListingCollectionMemberDetail {
  productId?: string;
  product_id?: string;
  [key: string]: unknown;
}

/** A member entry: a bare listing id, or a (possibly sparse) product record. */
export type RawListingCollectionMember =
  | string
  | RawListingCollectionMemberDetail;

export interface RawListingCollection {
  uid: string;
  creatorId?: string;
  creatorName?: string | null;
  collectionName?: string;
  collection?: RawListingCollectionMember[] | null;
  bannerUrl?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface RawListingCollectionListResponse {
  contents: RawListingCollection[] | null;
}

// ─── Errors ──────────────────────────────────────────────────────────────────

/** Backend error codes for the listing-collection endpoints (HTTP 200 envelope). */
export const ListingCollectionErrorCodes = {
  /** GET/PUT/DELETE with a non-existent collection id. */
  NOT_FOUND: "15006",
  /** GET /listing-collections with neither name nor creatorId. */
  VALIDATION: "15007",
  /** POST/PUT with a missing or blank collectionName. */
  NAME_REQUIRED: "15008",
  /** POST/PUT with missing or empty listingIds. */
  LISTING_IDS_REQUIRED: "15009",
  /** POST with no resolvable caller identity (defense-in-depth). */
  UNAUTHENTICATED: "15010",
  /** Any uncaught exception or wrapped ProductService failure. */
  INTERNAL: "15999",
} as const;

export type ListingCollectionErrorCode =
  (typeof ListingCollectionErrorCodes)[keyof typeof ListingCollectionErrorCodes];

/**
 * The failure envelope these endpoints return — failures reported inside a
 * 2xx body, so `response.ok` alone isn't enough to trust a payload.
 */
export interface ListingCollectionErrorEnvelope {
  errorInformation?:
    | { errorCode?: string; errorDetail?: string | null }[]
    | null;
  generalProcessingStatus?: {
    processingStatus?: string;
    processingDetails?: string | null;
  } | null;
}
